import { apiClient, parseResp } from "@shared/api";
import {
  DeviceAuthorizationInfoSchema,
  CloudProjectListSchema,
  CloudProjectOpenedSchema,
  GitServerCheckSchema,
  GitProjectPageSchema,
  GitServerListSchema,
  GitServerLoginSchema,
  ProjectGitRemoteSchema,
  type CloudProjectList,
  type CloudProjectOpened,
  type DeviceAuthorizationInfo,
  type DeviceDecisionRequest,
  type GitServerCheck,
  type GitProjectPage,
  type GitServerList,
  type GitServerLogin,
  type GitServerLoginRequest,
  type ProjectGitRemote,
} from "../model/gitServer.schema";

// Ошибки показывают сами экраны (итог на странице /device, текст в мастере):
// общий тост клиента здесь лишний.
const quiet = { silentError: true } as const;
const enc = encodeURIComponent;

/** Страница /device на сервере: что просит устройство и решение человека (раздел 7.4). */
export const deviceApi = {
  info: (userCode: string): Promise<DeviceAuthorizationInfo> =>
    apiClient
      .get(`/account/device/${enc(userCode)}`, quiet)
      .then(parseResp(DeviceAuthorizationInfoSchema)),
  decide: (
    userCode: string,
    body: DeviceDecisionRequest,
  ): Promise<DeviceAuthorizationInfo> =>
    apiClient
      .post(`/account/device/${enc(userCode)}`, body, quiet)
      .then(parseResp(DeviceAuthorizationInfoSchema)),
};

/** Адрес клонирования проекта на сервере (раздел 7.6). */
export const projectGitRemoteApi = {
  get: (projectId: string): Promise<ProjectGitRemote> =>
    apiClient
      .get(`/projects/${enc(projectId)}/git-remote`, quiet)
      .then(parseResp(ProjectGitRemoteSchema)),
};

/** Локальная Студия: вход на сервер Студии кодом и его проекты (раздел 7.5). */
export const gitServersApi = {
  list: (): Promise<GitServerList> =>
    apiClient.get("/git/servers", quiet).then(parseResp(GitServerListSchema)),
  /** Сервер перед подключением: его версия против нашей и уже сохранённый вход. */
  check: (url: string): Promise<GitServerCheck> =>
    apiClient
      .get("/git/servers/check", { ...quiet, params: { url } })
      .then(parseResp(GitServerCheckSchema)),
  login: (body: GitServerLoginRequest): Promise<GitServerLogin> =>
    apiClient
      .post("/git/servers/login", body, quiet)
      .then(parseResp(GitServerLoginSchema)),
  loginState: (loginId: string): Promise<GitServerLogin> =>
    apiClient
      .get(`/git/servers/login/${enc(loginId)}`, quiet)
      .then(parseResp(GitServerLoginSchema)),
  cancelLogin: (loginId: string): Promise<void> =>
    apiClient
      .delete(`/git/servers/login/${enc(loginId)}`, quiet)
      .then(() => undefined),
  projects: (
    credentialId: string,
    params: { q?: string; limit?: number; offset?: number } = {},
  ): Promise<GitProjectPage> =>
    apiClient
      .get(`/git/servers/${enc(credentialId)}/projects`, {
        ...quiet,
        params,
      })
      .then(parseResp(GitProjectPageSchema)),
  /** Проекты человека на всех серверах, куда вошла эта Студия, с пометкой «открыт здесь». */
  cloudProjects: (): Promise<CloudProjectList> =>
    apiClient
      .get("/git/cloud/projects", quiet)
      .then(parseResp(CloudProjectListSchema)),
  /** Открыть проект сервера на компьютере: уже открыт или скачивается в стандартную папку. */
  openProject: (
    credentialId: string,
    projectId: string,
  ): Promise<CloudProjectOpened> =>
    apiClient
      .post(
        `/git/servers/${enc(credentialId)}/projects/${enc(projectId)}/open`,
        undefined,
        quiet,
      )
      .then(parseResp(CloudProjectOpenedSchema)),
  forget: (credentialId: string): Promise<void> =>
    apiClient
      .delete(`/git/servers/${enc(credentialId)}`, quiet)
      .then(() => undefined),
};
